import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { executive, CANONICAL_ORIGIN } from "@/data/site";

const details = {
  "Private Equity": "Deal sourcing, diligence support, structuring, and value-creation planning for middle-market private equity opportunities across control and minority positions.",
  "Strategic Advisory": "We help businesses scale through strategic partnerships and prepare for financing through financial modeling, business planning, and funding-readiness assessments.",
  "Business Development": "Identification and execution of strategic partnerships, distribution relationships, and enterprise sales relationships that scale revenue durably.",
  "Strategic Partnerships": "Structuring institutional partnerships, joint ventures, and cross-industry collaborations designed for long-term compounding.",
  "Consumer Brands": "Advisory to founder-led and sponsor-backed consumer businesses focused on brand equity, distribution economics, and shelf strategy.",
  "Retail Expansion": "Door-level economics, national account strategy, and disciplined multi-channel expansion for growth-stage brands.",
  "Manufacturing Growth": "Capital formation, throughput optimization, and industrial partnerships supporting the reshoring and modernization of the U.S. industrial base.",
  "Investment Strategy": "Portfolio construction, allocation frameworks, and long-duration capital deployment strategies tailored to institutional mandates.",
  "Business Acquisitions": "End-to-end acquisition support: target identification, diligence, structuring, and post-close operational integration.",
  "Capital Raising": "Institutional investor readiness, materials architecture, and process management for equity and structured capital raises.",
  "Commercial Real Estate": "Advisory on commercial real estate opportunities across office, industrial, retail, multifamily, and mixed-use assets — sourcing, capital structure, and joint-venture partnerships.",
  "Real Estate Funds": "Fund formation strategy, LP relationship development, and deployment discipline for real estate funds targeting core-plus, value-add, and opportunistic mandates.",
};

export default function Expertise() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    url: `${CANONICAL_ORIGIN}/leadership/expertise`,
    name: "Areas of Focus — Adeshi Moffi",
    about: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
    hasPart: executive.expertise.map((e) => ({
      "@type": "Service",
      name: e,
      provider: { "@id": `${CANONICAL_ORIGIN}/#organization` },
    })),
  };

  return (
    <>
      <Seo
        title="Areas of Focus — Adeshi Moffi | Investors Pathway"
        description="Advisory focus led by Adeshi Moffi at Investors Pathway LLC: private equity, strategic advisory, business development, strategic partnerships, consumer brands, retail expansion, manufacturing growth, investment strategy, business acquisitions, and capital raising."
        path="/leadership/expertise"
        image={executive.headshot}
        jsonLd={jsonLd}
      />
      <section className="pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Leadership", to: "/leadership" },
              { label: "Focus" },
            ]}
          />
          <Reveal>
            <div className="mt-10 section-num">04 / Areas of Focus</div>
            <h1
              data-testid="expertise-title"
              className="h-display text-[3rem] md:text-[4.6rem] text-[#0A1128] mt-6 max-w-4xl"
            >
              Twelve disciplines. One operating philosophy.
            </h1>
            <p className="mt-6 body-lead max-w-3xl">
              A concentrated advisory practice, applied at institutional standards
              across the middle-market capital stack.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <ul className="border-t border-[#E2E8F0]" data-testid="expertise-list">
            {executive.expertise.map((e, i) => (
              <Reveal
                as="li"
                key={e}
                className="border-b border-[#E2E8F0] py-10 md:py-14 grid grid-cols-12 gap-6 md:gap-10 items-baseline"
              >
                <div className="col-span-12 md:col-span-2">
                  <span className="section-num text-[#8E7331]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <h2 className="h-editorial text-3xl md:text-4xl text-[#0A1128] leading-tight">
                    {e}
                  </h2>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <p className="body-lead">{details[e]}</p>
                </div>
              </Reveal>
            ))}
          </ul>

          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/leadership/contact" data-testid="expertise-contact-cta" className="btn-ink">
              Discuss a Mandate <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
            <Link to="/leadership/thought-leadership" data-testid="expertise-thought-cta" className="btn-outline">
              Read Perspectives
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
